import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import PreLoginLayout from './PreLoginLayout';
import ConfirmEmail from './ConfirmEmail';
import ResetPassword from './ResetPassword';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import useVenueSetupMode from './hooks/useVenueSetupMode';
import VenueOwnerRoute from './components/VenueOwnerRoute';
import BoundaryAnimatedShell from './components/transitions/BoundaryAnimatedShell';
import LoaderScreen from './components/LoaderScreen';
import { HeroLoopGate } from './components/HeroSportsVideo';
import TournamentInviteLanding from './components/UniversalLinkHandler';
import { CREDIT_BILLED_ACADEMY_ID } from './lib/academyCreditBilling';

import {
  Login,
  CreateProfile,
  VerifyPhone,
  Profile,
  AdminLayout,
  AdminDashboard,
  AdminOverview,
  GameCheck,
  AdminAssemblies,
  Audit,
  MatchSubmissions,
  MatchSubmitted,
  ReplaySubmissions,
  ReplaysSubmitted,
  ChallengesAdmin,
  VideoAnalysis,
  HighlightsReview,
  VenueQRCodes,
  VenueOwnerLayout,
  VenueGames,
  VenueGamesHistory,
  VenueGamesCodes,
  VenueReplays,
  VenueBookings,
  VenueSetupWelcome,
  VenueSettings,
  VenueOwnerLogin,
  ShortLinkRedirect,
  ReplayLinkHandler,
  JoinLinkHandler,
  LegacyJoinQueryRedirect,
  UniversalLinkHandler,
  ThreeDSCallback,
  AppDownload,
  UploadFootage,
  PublicMatchView,
  PlayerGamePayment,
  LeagueTeamPayment,
  AcademyCreditPayment,
  VenueReplayLinks,
  TestSubscription,
  PaymentSuccess,
  PaymentDecline,
  PaymentCancel,
  AcademyLogin,
  AcademyLayout,
  AcademyDashboard,
  AcademySchedule,
  AcademyFreeReplay,
  AcademyShareGames,
  AcademyTeamDetails,
  AcademyPlayerDetails,
  AcademyMatchDetails,
  AcademyTimestampAnalysis,
  AcademySettings,
  AcademyDrills,
  AcademyCreateDrill,
  AcademyTeamsHub,
  AcademyRoster,
  AcademyMatches,
  AcademyLeaguesHub,
  AcademyLeagueDetail,
  AcademyLeagueMatchCenter,
  AcademyLeagueTeamProfile,
  AcademyTrainingDetails,
  AcademyVideoSessions,
  AcademySessionBuilder,
  AcademySessionPresenter,
  SessionDesigner,
  JoinAcademy,
  JoinAcademyLeague,
  AcademyCoachInviteSignIn,
  AcademyCoachInviteProfileRedirect,
  LegacyAcademyJoinRedirect,
  AdminSignups,
  AdminInvoices,
  CommunityLogin,
  CommunitySignup,
  CommunityLayout,
  CommunityDashboard,
  CommunitySchedule,
  CommunityMatches,
  CommunityGrowthPage,
  CommunityStats,
  CommunityLeaderboard,
  CommunityChallenges,
  CommunityChallengeDetail,
  CommunityRetentionDeepDive,
  CommunityGameSuccessDeepDive,
  CommunityDiscoveryAnalytics,
  CommunityEngagementDeepDive,
  CommunityPlayerManagement,
  CommunityJoinRequests,
  CommunityGroups,
  ManagementHub,
  CommunityPlayerDetails,
  CommunityMatchDetails,
  CommunityVideoLibrary,
  ConfigSettings,
  CommunityActionRequired,
  CommunityCompetitions,
  CreateCompetition,
  CompetitionDetail,
  BuyCreditsSecure,
  AcademyTeamSubscribePage,
  JoinCommunityManager,
  CommunityManagerInviteSignIn,
  AcademyManagement,
  CommunityManagement,
  AdminApiKeys,
  AdminApiHistory,
  AdminPadelytics,
  AdminPadelyticsSession,
  AdminSentMail,
  AdminReferral,
  AdminEvents,
  EventBoothRoute,
  VenueReferralPage,
  ApiDocumentation,
} from './lazyRoutes';

// Unified signup flow — all /signup/* routes live inside this wrapper
// which owns the slide+fade transitions between funnel screens.
import SignupRoutes from './components/signup/SignupRoutes';
// Full-screen "completing setup" climax that plays between the
// wizard's final step and the Welcome page. Mounted at app root so
// it survives the wizard route unmounting after Continue.
import SetupCompletingTransitionMount from './components/signup/SetupCompletingTransition';
import { BackgroundUploadProvider } from './contexts/BackgroundUploadContext';
import SetupVenueCompletingTransitionMount from './components/signup/SetupVenueCompletingTransition';
import RouteSeo from './components/RouteSeo';
import NotFound from './pages/NotFound';
import { BlogArticle, BlogIndex } from './pages/Blog';

// Setup-mode portal preview (post-signup, pre-activation)
import SetupModeWelcome from './components/setup/SetupModeWelcome';
import SetupModeBookCall from './components/setup/SetupModeBookCall';

// Client-facing portal chooser shown at /login
import LoginStart from './components/login/LoginStart';

import ProtectedRoute from './ProtectedRoute';
import CommunityRoute from './components/CommunityRoute';
import AdminRoute from './components/AdminRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import AcademyRoute from './components/AcademyRoute';

/**
 * Venue portal index resolver — picks Welcome (in setup mode) or Games
 * (in active mode) for /venue-owner. Kept inline here because it's
 * pure routing glue and lives next to the routes it serves; the hook
 * import is already pulled in for App's SDK code.
 */
const VenueIndexRedirect = () => {
  const { isSetupMode } = useVenueSetupMode();
  if (isSetupMode) return <Navigate to="/venue-owner/welcome" replace />;
  return <VenueGames />;
};

const RouteFallback = () => <LoaderScreen delay={0} title="Loading" />;

const App = () => {
  return (
    <Router>
      <HeroLoopGate />
      <BackgroundUploadProvider>
      <RouteSeo />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Redirect removed contact page to home */}
        <Route path="/contact" element={<Navigate to="/" replace />} />

        {/* Redirect old landingpage URL to home */}
        <Route path="/landingpage" element={<Navigate to="/" replace />} />
        
        {/* Redirect old dashboard URL to home */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />

        {/* Pricing is hidden from public view during the demo-booking campaign.
            Redirect direct visits to the features section on home. */}
        <Route path="/pricing" element={<Navigate to="/" state={{ scrollTo: 'features' }} replace />} />

        {/* Boundary-animated public group.
            Landing (/), the entire /signup/* funnel, and the post-signup
            /community/welcome page share a single parent route so that
            BoundaryAnimatedShell stays mounted across the boundaries
            and AnimatePresence can run cross-group transitions:
              - Landing → Signup: 200ms fade-out + 350ms fade-in/translate
              - Signup → Welcome: 250ms fade-out + 400ms fade-in/translate
            Within-signup transitions are still owned by SignupRoutes'
            own AnimatePresence (slide+fade between steps).
            The child routes below carry no element — they exist only so
            React Router knows which paths the parent matches; the actual
            page elements are rendered by the inner Routes inside
            BoundaryAnimatedShell. */}
        <Route element={<BoundaryAnimatedShell />}>
          <Route index                          element={null} />
          <Route path="book-demo"               element={null} />
          <Route path="sports/:sport"           element={null} />
          <Route path="solutions/:audience"     element={null} />
          <Route path="signup/*"                element={null} />
        </Route>

        {/* Client-facing login — portal chooser (Community / Academy / Venue),
            then hands off to the matching portal login for OAuth sign-in. */}
        <Route path="/login" element={<LoginStart />} />
        <Route path="/upload" element={<Navigate to="/?upload=1" replace />} />
        <Route path="/upload/pay" element={<BuyCreditsSecure />} />
        {/* Staff sign-in (kept off the client chooser to avoid confusion). */}
        <Route path="/admin/login" element={<Login />} />

        {/* Setup-mode portal — what users see after signup, before an admin
            activates them. FE-only: routes are unguarded (the colleague will
            wrap each one with the matching CommunityRoute / AcademyRoute /
            VenueOwnerRoute when backend wiring is in).
            Note: /community/welcome is handled inside BoundaryAnimatedShell
            above so the wizard → portal transition can animate. */}
        <Route path="/community/book-activation-call"   element={<Navigate to="/community/dashboard" replace />} />
        <Route path="/academy/welcome"                  element={<SetupModeWelcome persona="academy" />} />
        <Route path="/academy/book-activation-call"     element={<SetupModeBookCall persona="academy" />} />
        {/* /venue-owner/welcome moved into the venue-owner parent route below
            so the new VenueSetupWelcome renders inside VenueOwnerLayout's
            chrome (sidebar, header, account menu). The shared
            SetupModeBookCall stays as a fallback route — existing links
            land somewhere coherent. */}
        <Route path="/venue-owner/book-activation-call" element={<SetupModeBookCall persona="venue" />} />

        {/* Pre-login Routes */}
        <Route element={<PreLoginLayout />}>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
        </Route>

        {/* Public API Documentation */}
        <Route path="/docs/api" element={<ApiDocumentation />} />

        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />

        {/* Academy Routes */}
        <Route path="/academy/login" element={<AcademyLogin />} />
        <Route path="/academy/invite/:academyId/sign-in" element={<AcademyCoachInviteSignIn />} />
        <Route path="/academy/invite/:academyId" element={<JoinAcademy />} />
        <Route path="/academy/join/profile" element={<AcademyCoachInviteProfileRedirect />} />
        <Route path="/academy/pending" element={<Navigate to="/signup/academy/book" replace />} />
        <Route
          path="/academy"
          element={
            <AcademyRoute>
              <AcademyLayout />
            </AcademyRoute>
          }
        >
          <Route index element={<AcademyDashboard />} />
          <Route path="dashboard" element={<AcademyDashboard />} />
          <Route path="sessions" element={<AcademyMatches />} />
          <Route path="matches" element={<AcademyMatches />} />
          <Route path="analysis" element={<Navigate to="/academy/dashboard" replace />} />
          <Route path="stats" element={<Navigate to="/academy/dashboard" replace />} />
          <Route path="schedule" element={<AcademySchedule />} />
          <Route path="roster" element={<AcademyRoster />} />
          <Route path="teams" element={<AcademyTeamsHub />} />
          <Route path="leagues" element={<AcademyLeaguesHub />} />
          <Route path="leagues/:competitionId" element={<AcademyLeagueDetail />} />
          <Route path="leagues/:competitionId/match/:fixtureId" element={<AcademyLeagueMatchCenter />} />
          <Route path="leagues/:competitionId/team/:teamId" element={<AcademyLeagueTeamProfile />} />
          <Route path="drills" element={<AcademyDrills />} />
          <Route path="drills/new" element={<AcademyCreateDrill />} />
          <Route path="management" element={<Navigate to="/academy/teams" replace />} />
          <Route path="team-management" element={<Navigate to="/academy/teams" replace />} />
          <Route path="players" element={<Navigate to="/academy/roster" replace />} />
          <Route path="free-replay" element={<AcademyFreeReplay />} />
          <Route path="share-games" element={<AcademyShareGames />} />
          <Route path="team/:teamId" element={<AcademyTeamDetails />} />
          <Route path="player/:playerId" element={<AcademyPlayerDetails />} />
          <Route path="match/:matchId" element={<AcademyMatchDetails />} />
          <Route path="training/:matchId" element={<AcademyTrainingDetails />} />
          <Route path="timestamp-analysis" element={<AcademyTimestampAnalysis />} />
          <Route path="settings" element={<AcademySettings />} />
          <Route path="staff" element={<Navigate to="/academy/teams" replace />} />
          <Route path="video-library" element={<Navigate to="/academy/video-sessions" replace />} />
          <Route path="video-sessions" element={<AcademyVideoSessions />} />
          {/* Session Designer — dev-only URL, not in sidebar nav (step 1 of build) */}
          <Route path="session-designer-dev" element={<SessionDesigner />} />
        </Route>
        <Route
          path="/academy/buy-credits"
          element={
            <AcademyRoute>
              <BuyCreditsSecure academyId={CREDIT_BILLED_ACADEMY_ID} />
            </AcademyRoute>
          }
        />
        <Route
          path="/academy/subscribe"
          element={
            <AcademyRoute>
              <AcademyTeamSubscribePage />
            </AcademyRoute>
          }
        />
        <Route path="/academy/video-sessions/builder/:sessionId" element={<AcademyRoute><AcademySessionBuilder /></AcademyRoute>} />
        <Route path="/academy/video-sessions/present/:sessionId" element={<AcademyRoute><AcademySessionPresenter /></AcademyRoute>} />

        {/* Community Routes */}
        <Route path="/community/login" element={<CommunityLogin />} />
        <Route path="/community/signup" element={<CommunitySignup />} />
        <Route path="/community/invite/:communityId/sign-in" element={<CommunityManagerInviteSignIn />} />
        <Route path="/community/invite/:communityId" element={<JoinCommunityManager />} />
        <Route path="/community/create-community" element={<Navigate to="/upload" replace />} />
        
        <Route
          path="/community/buy-credits"
          element={
            <CommunityRoute>
              <BuyCreditsSecure />
            </CommunityRoute>
          }
        />

        <Route
          path="/community"
          element={
            <CommunityRoute>
              <CommunityLayout />
            </CommunityRoute>
          }
        >
          <Route index element={<CommunityDashboard />} />
          <Route path="dashboard" element={<CommunityDashboard />} />
          <Route path="welcome" element={<Navigate to="/community/dashboard" replace />} />
          <Route path="management" element={<ManagementHub />} />
          <Route path="join-requests" element={<Navigate to="/community/management" replace state={{ tab: 'join-requests' }} />} />
          <Route path="player-management" element={<Navigate to="/community/management" replace />} />
          <Route path="groups" element={<Navigate to="/community/management" replace state={{ tab: 'groups' }} />} />
          <Route path="matches" element={<CommunityMatches />} />
          <Route path="competitions" element={<CommunityCompetitions />} />
          <Route path="competitions/create" element={<CreateCompetition />} />
          <Route path="competitions/create-league" element={<CreateCompetition />} />
          <Route path="competitions/:competitionId" element={<CompetitionDetail />} />
          <Route path="growth" element={<CommunityGrowthPage />} />
          <Route path="leaderboard" element={<CommunityLeaderboard />} />
          <Route path="challenges" element={<CommunityChallenges />} />
          <Route path="challenges/:challengeId" element={<CommunityChallengeDetail />} />
          <Route path="stats" element={<CommunityStats />} />
          <Route path="analytics/retention" element={<CommunityRetentionDeepDive />} />
          <Route path="analytics/game-success" element={<CommunityGameSuccessDeepDive />} />
          <Route path="analytics/growth" element={<Navigate to="/community/stats" replace />} />
          <Route path="analytics/engagement" element={<CommunityEngagementDeepDive />} />
          <Route path="analytics/discovery" element={<CommunityDiscoveryAnalytics />} />
          <Route path="schedule" element={<CommunitySchedule />} />
          <Route path="player/:playerId" element={<CommunityPlayerDetails />} />
          <Route path="match/:matchId" element={<CommunityMatchDetails />} />
          <Route path="video-library" element={<CommunityVideoLibrary />} />
          <Route path="timestamp-analysis" element={<Navigate to="/community/video-library" replace />} />
          <Route path="settings" element={<Navigate to="/community/settings-config" replace />} />
          <Route path="community-config" element={<Navigate to="/community/settings-config" replace state={{ tab: 'community' }} />} />
          <Route path="settings-config" element={<ConfigSettings />} />
          <Route path="zones-config" element={<Navigate to="/community/settings-config" replace />} />
          <Route path="locations-config" element={<Navigate to="/community/settings-config" replace state={{ tab: 'locations' }} />} />
          <Route path="game-defaults-config" element={<Navigate to="/community/settings-config" replace />} />
          <Route path="join-policy-config" element={<Navigate to="/community/settings-config" replace state={{ tab: 'locations' }} />} />
          <Route path="action-required" element={<CommunityActionRequired />} />
        </Route>

        {/* Venue Owner - Independent (no main Layout) */}
        <Route path="/venue-owner/login" element={<VenueOwnerLogin />} />
        <Route
          path="/venue-owner"
          element={
            <VenueOwnerRoute>
              <VenueOwnerLayout />
            </VenueOwnerRoute>
          }
        >
          {/* Games is the home in active mode; in setup mode the Welcome
              page is the landing surface so the activation CTA is the
              first thing the user sees. VenueIndexRedirect picks per
              setup-mode state. */}
          <Route index element={<VenueIndexRedirect />} />
          <Route path="welcome" element={<VenueSetupWelcome />} />
          <Route path="games" element={<VenueGames />} />
          <Route path="history" element={<VenueGamesHistory />} />
          <Route path="games/history" element={<Navigate to="/venue-owner/history" replace />} />
          <Route path="games/codes" element={<VenueGamesCodes />} />
          <Route path="games/live" element={<Navigate to="/venue-owner/replays" replace />} />
          <Route path="replays" element={<VenueReplays />} />
          {/* Old create-game wizard route — Games is now action-based, send to bookings */}
          <Route path="games/create" element={<Navigate to="/venue-owner/bookings" replace />} />
          <Route path="bookings" element={<VenueBookings />} />
          <Route path="settings" element={<VenueSettings />} />
          <Route path="settings/courts" element={<VenueSettings />} />
          {/* Legacy paths — redirect to the current surface */}
          <Route path="today" element={<Navigate to="/venue-owner/games" replace />} />
          <Route path="money" element={<Navigate to="/venue-owner/games" replace />} />
          <Route path="revenue" element={<Navigate to="/venue-owner/games" replace />} />
          <Route path="customers" element={<Navigate to="/venue-owner/games" replace />} />
          <Route path="tools" element={<Navigate to="/venue-owner/games" replace />} />
        </Route>

        {/* Short Link Redirect */}
        <Route path="/s/:slug" element={<ShortLinkRedirect />} />

        {/* Legacy /join?code=… → /create/join; bare /join → AppDownload fallback */}
        <Route path="/join" element={<LegacyJoinQueryRedirect />} />

        {/* Public invite links — open app when installed, AppDownload fallback on web */}
        <Route path="/join/league/:token" element={<JoinAcademyLeague />} />
        <Route path="/join/academy/:academyId" element={<LegacyAcademyJoinRedirect />} />
        <Route path="/join/game/:matchId" element={<UniversalLinkHandler />} />
        <Route path="/join/tournament/:id" element={<TournamentInviteLanding />} />
        <Route path="/join/:idOrSlug" element={<UniversalLinkHandler />} />

        {/* 3D Secure Callback - handles bank verification redirect */}
        <Route path="/3ds-callback" element={<ThreeDSCallback />} />

        {/* Secure Acceptance Payment Result Pages */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/decline" element={<PaymentDecline />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/pay/game/:token" element={<PlayerGamePayment />} />
        <Route path="/pay/league/:token" element={<LeagueTeamPayment />} />
        <Route path="/pay/credits/:token" element={<AcademyCreditPayment />} />

        {/* App Download Page - for universal links without app */}
        <Route path="/app-download" element={<AppDownload />} />
        <Route path="/refer" element={<VenueReferralPage />} />
        <Route path="/events/:slug" element={<EventBoothRoute />} />

        {/* Universal Link Routes - attempts to open app, falls back to app download */}
        {/* Specific handlers for routes that need special handling */}
        <Route path="/matches/modals/venue/replay-selection/replay/:pitchSlug" element={<ReplayLinkHandler />} />
        <Route path="/create/join" element={<JoinLinkHandler />} />

        {/* Generic universal link handlers for all other universal link patterns */}
        {/* Match routes - more specific routes first */}
        <Route path="/matches/modals/venue/*" element={<UniversalLinkHandler />} />
        <Route path="/matches/*" element={<UniversalLinkHandler />} />
        <Route path="/match/*" element={<UniversalLinkHandler />} />
        
        {/* Game routes */}
        <Route path="/game/*" element={<UniversalLinkHandler />} />
        
        {/* Leaderboard routes */}
        <Route path="/leaderboards/*" element={<UniversalLinkHandler />} />
        
        {/* Profile routes */}
        <Route path="/feed/modals/profile/*" element={<UniversalLinkHandler />} />
        
        {/* Challenge routes */}
        <Route path="/feed/modals/challenge/*" element={<UniversalLinkHandler />} />
        <Route path="/challenge/*" element={<UniversalLinkHandler />} />

        {/* Public Upload Page - for shareable upload links */}
        <Route path="/upload/:token" element={<UploadFootage />} />
        <Route path="/view/:token" element={<PublicMatchView />} />

        {/* Public Venue Replay Links Page - for QR code access */}
        <Route path="/venue-replay" element={<VenueReplayLinks />} />
        <Route path="/court/:venueId/:pitch" element={<UniversalLinkHandler />} />
        <Route path="/matches/court/:venueId/:pitch" element={<UniversalLinkHandler />} />

        {/* Post-login Routes - Phone verification then profile creation */}
        <Route path="/verify-phone" element={<VerifyPhone />} />
        <Route path="/create-profile" element={<CreateProfile />} />

        {/* Test Subscription Page - Protected route for testing Cybersource */}
        <Route 
          path="/test-subscription" 
          element={
            <ProtectedRoute>
              <TestSubscription />
            </ProtectedRoute>
          } 
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          {/* Video Analysis - Available to all admins */}
          <Route path="video-analysis" element={<VideoAnalysis />} />
          <Route path="highlights-review" element={<HighlightsReview />} />
          <Route path="game-check" element={<GameCheck />} />
          <Route path="venue-qr-codes" element={<VenueQRCodes />} />

          {/* Super Admin Only Routes */}
          <Route index element={<VideoAnalysis />} />
          <Route path="overview" element={<SuperAdminRoute><AdminOverview /></SuperAdminRoute>} />
          <Route path="assemblies" element={<SuperAdminRoute><AdminAssemblies /></SuperAdminRoute>} />
          <Route path="dashboard" element={<SuperAdminRoute><AdminDashboard /></SuperAdminRoute>} />
          <Route path="audit" element={<SuperAdminRoute><Audit /></SuperAdminRoute>} />
          <Route path="match-submissions" element={<SuperAdminRoute><MatchSubmissions /></SuperAdminRoute>} />
          <Route path="match-submitted" element={<SuperAdminRoute><MatchSubmitted /></SuperAdminRoute>} />
          <Route path="academies" element={<SuperAdminRoute><AcademyManagement /></SuperAdminRoute>} />
          <Route path="communities" element={<SuperAdminRoute><CommunityManagement /></SuperAdminRoute>} />
          <Route path="events" element={<SuperAdminRoute><AdminEvents /></SuperAdminRoute>} />
          <Route path="events/:slug" element={<SuperAdminRoute><AdminEvents /></SuperAdminRoute>} />
          <Route path="referral" element={<SuperAdminRoute><AdminReferral /></SuperAdminRoute>} />
          <Route path="replay-submissions" element={<SuperAdminRoute><ReplaySubmissions /></SuperAdminRoute>} />
          <Route path="replays-submitted" element={<SuperAdminRoute><ReplaysSubmitted /></SuperAdminRoute>} />
          <Route path="challenges" element={<SuperAdminRoute><ChallengesAdmin /></SuperAdminRoute>} />
          <Route path="signups" element={<SuperAdminRoute><AdminSignups /></SuperAdminRoute>} />
          <Route path="invoices" element={<SuperAdminRoute><AdminInvoices /></SuperAdminRoute>} />
          <Route path="api-keys" element={<SuperAdminRoute><AdminApiKeys /></SuperAdminRoute>} />
          <Route path="api-history" element={<SuperAdminRoute><AdminApiHistory /></SuperAdminRoute>} />
          <Route path="padelytics/:sessionUuid" element={<SuperAdminRoute><AdminPadelyticsSession /></SuperAdminRoute>} />
          <Route path="padelytics" element={<SuperAdminRoute><AdminPadelytics /></SuperAdminRoute>} />
          <Route path="sent-mail" element={<SuperAdminRoute><AdminSentMail /></SuperAdminRoute>} />
        </Route>

        {/* FE-only preview route — lets you see /admin/signups without auth.
            DELETE this route once the colleague wires Supabase + admin login. */}
        <Route path="/preview/admin/signups" element={<AdminSignups />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>

      {/* App-root overlay slot for the "completing setup" climax. Sits
          above every route so the loader can outlive the wizard route's
          unmount when the Continue handler navigates to the Welcome
          page. The mount is always rendered; the loader itself only
          appears when the controller pub/sub fires. Two parallel mounts
          for the two wizard variants (community / venue) — each
          subscribes to its own controller, only the matching one ever
          fires. */}
      <SetupCompletingTransitionMount />
      <SetupVenueCompletingTransitionMount />
      </BackgroundUploadProvider>
    </Router>
  );
};

export default App; 
